import React, { useEffect } from 'react';
import { useFormik, FormikValues } from 'formik';
import { CompensationSentence } from 'types';
import { Validators } from 'core';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { path } from 'ramda';

import FormError from 'components/molecules/form-error';
import { ClearStateSliceActions, compensationSentenceSelectors } from 'common/config/store';
import Input from 'components/organisms/input';
import { useIntl } from 'common/config/i18n';
import Button from 'components/molecules/button';
import { StyledRepositoryForm, StyledRepositoryFormButtonWrapper } from 'common/styling/mixins';

import { Editor } from '../react-quill';

const Label = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

interface Props {
  onSubmit: (formValues: FormikValues) => void;
  compensationSentence?: CompensationSentence;
}

const FormCompensationSentence: React.FC<Props> = (props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const status = useSelector(compensationSentenceSelectors.statusSelector);
  const error = useSelector(compensationSentenceSelectors.errorSelector);

  useEffect(() => {
    dispatch(
      ClearStateSliceActions.clearStateSlice({
        sliceReducer: 'CompensationSentence',
      })
    );
  }, [dispatch]);

  const initialValues = {
    id: props?.compensationSentence?.id || undefined,
    longMessage: props?.compensationSentence?.longMessage || '',
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.compensationSentenceInfo(values),
    onSubmit: (values) => {
      props.onSubmit(values);
    },
  });

  return (
    <StyledRepositoryForm onSubmit={formik.handleSubmit}>
      <div className="formSection">
        <Input
          upLabel={t('CompensationSentencePage', 'NAME')}
          name="compensationName"
          value={props?.compensationSentence?.name || ''}
          width="65%"
          contentEditable={false}
        />
      </div>

      <div className="formSection">
        <Label>{t('CompensationSentencePage', 'LONG_MSG')}</Label>
        <Editor
          value={formik.values.longMessage}
          onChange={({ content, textWithoutHtml }) => {
            if (content && textWithoutHtml) {
              if (textWithoutHtml.trim().length === 0) {
                formik.setFieldValue('longMessage', '');
              } else {
                formik.setFieldValue('longMessage', content);
              }
            }
          }}
        />
        <FormError formik={formik} fieldName="longMessage" />
      </div>

      <StyledRepositoryFormButtonWrapper>
        <Button
          height="35px"
          width="100%"
          borderRadius="20px"
          disabled={!!(formik.errors.longMessage && formik.touched.longMessage)}
          htmlType="submit"
          loading={status === 'loading'}
        >
          {t('Buttons', 'SAVE_BUTTON')}
        </Button>
      </StyledRepositoryFormButtonWrapper>
      {error && <FormError message={t('form', 'FORM_UNKNOWN_ERROR')} />}
    </StyledRepositoryForm>
  );
};

export default FormCompensationSentence;
